import {
    resolveEntityPhotoUrl,
    entityPhotoHasImage,
    activeDomainImage,
} from '../../../utils/imageProxy';
import { ensureSignedProxyUrls } from '../../../utils/signedImageProxyCache';
import { CustomErrorLogger } from '../../../utils/CustomErrorLogger';
import userDefaultImage from '../../../images/machineImageEmpty.svg';
import { useHasPermission } from '../../../hooks/useHasPermission';
import { EntityImageEditor } from '../../entityImage/EntityImageEditor';
import EntityPhotoFrame from '../../entityImage/EntityPhotoFrame';
import { deleteEntityImage } from '../../entityImage/entityImageApi';
import { syncListEntityImage } from '../../entityImage/entityImageListSync';
import { getEntityImageConfig } from '../../entityImage/entityImageConfig';
import { useSelector, useDispatch } from 'react-redux';
import OpenImageDialog from '../../openImageDialog';
import { DeleteRecord } from '../../deleteRecord';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useState, useCallback, useMemo } from 'react';
import Iconify from '../../Iconify';
import '../../../css/sites/siteImageActions.css';

/** Folder cover — unified editor (phase 3.4), legacy `article_folder_document` still read. */
const ArticleFolderImageActions = () => {

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const config = useMemo(() => getEntityImageConfig('articleFolder'), []);
    const articleFolderDetailsData = useSelector(config.detailsSelector);
    const canEdit = useHasPermission(config.permission);

    const [openImage, setOpenImage] = useState(false);
    const [deleteImage, setDeleteImage] = useState(false);
    const [modifyPhoto, setModifyPhoto] = useState(false);

    const photoOpts = useMemo(
        () => ({ imageField: config.imageField, documentField: config.documentField }),
        [config.imageField, config.documentField],
    );

    const displayUrl = resolveEntityPhotoUrl(articleFolderDetailsData, {
        ...photoOpts,
        size: 'medium',
        fallback: userDefaultImage,
    });
    const fullUrl = resolveEntityPhotoUrl(articleFolderDetailsData, {
        ...photoOpts,
        size: 'default',
        fallback: '',
    });
    const hasImage = entityPhotoHasImage(articleFolderDetailsData, { ...photoOpts, size: 'medium' });

    const openImageEditor = useCallback(() => {
        const activeImage = activeDomainImage(articleFolderDetailsData, config.imageField);
        if (activeImage?.key_source) {
            ensureSignedProxyUrls([activeImage.key_source], { silent: true });
        }
        setModifyPhoto(true);
    }, [config.imageField, articleFolderDetailsData]);

    const handleDeleteImage = useCallback(async () => {
        try {
            if (!articleFolderDetailsData?.id) {
                throw new Error('Aucun identifiant disponible.');
            }
            const response = await deleteEntityImage(config, articleFolderDetailsData.id);
            if (response.status === 200 || response.status === 201) {
                setDeleteImage(false);
                dispatch(config.setDetailsData({
                    ...articleFolderDetailsData,
                    [config.imageField]: [],
                    [config.documentField]: null,
                }));
                syncListEntityImage({
                    entityId: articleFolderDetailsData.id,
                    imageField: config.imageField,
                    image: null,
                    documentField: config.documentField,
                });
            }
        } catch (error) {
            console.error(t("Erreur lors de la suppression de l'image de dossier :"), error);
            CustomErrorLogger(error, 'ArticleFolderImageActions.delete');
        }
    }, [config, dispatch, articleFolderDetailsData, t]);

    const activeImage = activeDomainImage(articleFolderDetailsData, config.imageField);

    return (
        <>
        <EntityPhotoFrame
            image={activeImage}
            src={displayUrl}
            alt={config.alt}
            className="siteImageActions_image_icon_box"
            showBlur={hasImage}
        >
            {hasImage ? (
                <Box>
                    {canEdit &&
                        <Box className='siteImageActions_edit_delete_icon_box' >
                            <Iconify className='siteImageActions_edit_icon' icon={"ic:outline-edit"} onClick={openImageEditor} />
                            <Iconify className='siteImageActions_delete_icon' icon={"ic:outline-delete"} onClick={() => setDeleteImage(true)} />
                        </Box>
                    }
                    <Iconify className='siteImageActions_maximize_icon' icon={"tabler:arrows-maximize"} onClick={() => setOpenImage(true)} />
                </Box>
            ) : (
                canEdit && <Iconify className='siteImageActions_plus_icon' icon={"ic:baseline-plus"} onClick={openImageEditor} />
            )}
        </EntityPhotoFrame>

            {modifyPhoto &&
                <EntityImageEditor
                    entityType="articleFolder"
                    entityId={articleFolderDetailsData?.id}
                    entityData={articleFolderDetailsData}
                    modifyPhoto={modifyPhoto}
                    setModifyPhoto={setModifyPhoto}
                />
            }

            {openImage &&
                <OpenImageDialog
                    openImage={openImage}
                    setOpenImage={setOpenImage}
                    profileUrl={fullUrl}
                />
            }

            {deleteImage &&
                <DeleteRecord
                    openDelete={deleteImage}
                    setOpenDelete={setDeleteImage}
                    handleDelete={handleDeleteImage}
                    title={t(config.deleteTitle)}
                />
            }
        </>
    )
}

export default ArticleFolderImageActions
